import { z } from "zod";

import { designerNodeDataSchema } from "../designerNode/index.ts";
import { semverSchema, slotIdSchema } from "../shared/index.ts";
import { nodeSlotPathSchema } from "./nodeSlotPathSchema.ts";

export const PORTABLE_DESIGNER_NODE_TYPE = "@skilitics/interact-designer:node";

export const PORTABLE_DESIGNER_NODE_VERSION = "0.2.0";

const externalNodeContextSchema = z.enum(["reference", "variable", "condition", "effect", "value"]);

export type ExternalNodeContext = z.infer<typeof externalNodeContextSchema>;

const basicNodeSpecSchema = z.object({
  typeId: z.string(),
  title: z.string(),
});

export type BasicNodeSpec = z.infer<typeof basicNodeSpecSchema>;

const externalLinkagesSchema = z.object({
  // the slot path to the subject node (for linking on import)
  slotPath: nodeSlotPathSchema,
  // these are nodes outside of the portable node subtree too. however we can only capture the path for finding definite analogs when importing
  externalNodeSlotPaths: z
    .array(
      // In zod 4 this could instead use .optional() on the tuple elements (.default() seems to
      // be broken, though.) There doesn't seem to be a nice way to get a good error message,
      // it just gives the "Array must contain at most N element(s)" message, but at least with
      // the longest first you can tell it's the N+1-th item that failed.
      z.union([
        z.tuple([z.number(), nodeSlotPathSchema, z.array(externalNodeContextSchema), basicNodeSpecSchema]),
        // temp hack for data broken by transform / trigger migration that added an empty {}
        // need to be careful to not match broken nodespec, and to match expected length but not make the result
        // items readonly but not return an array... arg.
        z
          .tuple([z.number(), nodeSlotPathSchema, z.array(externalNodeContextSchema), z.strictObject({})])
          .transform(([id, slotPath, context]) => [id, slotPath, context] satisfies [unknown, unknown, unknown]),
        z.tuple([z.number(), nodeSlotPathSchema, z.array(externalNodeContextSchema)]),
        z.tuple([z.number(), nodeSlotPathSchema]),
      ]),
    )
    .optional(),
  // these are nodes outside of the portable node subtree that can be merged in on import such as when referenced by layout components
  dereferencedNodes: z.array(designerNodeDataSchema).optional(),
});

export type ExternalLinkages = z.infer<typeof externalLinkagesSchema>;

export const portableNodeSource = z.object({
  hostId: z.string(),
  slotId: slotIdSchema,
  origin: z.string().url(),
  action: z.enum(["cut", "copy", "save"]).default("copy"),
  appVersion: semverSchema,
});

export type PortableNodeSource = z.infer<typeof portableNodeSource>;

export const portableDesignerNodeInContextSchema = z.object({
  type: z.literal(PORTABLE_DESIGNER_NODE_TYPE), // might be mime type if we did clipboard item instead
  version: z.string().optional(),
  source: portableNodeSource,
  node: designerNodeDataSchema,
  linkages: externalLinkagesSchema.optional(),
});

export type PortableDesignerNodeInContext = z.infer<typeof portableDesignerNodeInContextSchema>;
